/**
 * Internal App-Theme Re-derivation Contract
 *
 * POST /internal/config/themes/rederive
 *
 * API-key-gated (`INTERNAL_API_KEY`). A one-purpose data-repair endpoint, not a
 * general theme writer: it re-runs `deriveThemeFromSeeds` over a school's
 * ALREADY-STORED `APP_THEME_SEEDS` pair and rewrites `APP_THEME` from the
 * result. It never invents seeds and never accepts colors in the request body —
 * a school with no stored seed pair is reported as skipped, not repaired.
 *
 * WHY IT EXISTS (SCH-1570). A stored palette is a SNAPSHOT of the derivation
 * that produced it, so a fix to the derivation reaches only schools that
 * re-save their brand in the web editor. When the derivation learned to hold
 * `primary`/`accent` away from the surface they are painted on, every palette
 * derived before that fix kept its collision — Miami Country Day's dark
 * `primary` and dark `background` were both `240 6% 10%`, so the active tab
 * label in the mobile person-detail tab bar was painted in the background
 * color and simply vanished. This endpoint replays the current derivation over
 * the seeds each school already picked, which is the only repair that keeps a
 * school's chosen brand while fixing its palette.
 *
 * IDEMPOTENT. A palette already equal to what the current derivation produces
 * reports `changed: false` and is not written. Re-running the endpoint after a
 * successful repair is a no-op.
 *
 * SEEDS ARE NEVER TOUCHED. Only the `APP_THEME` row is rewritten; the
 * `APP_THEME_SEEDS` row is the input and is left exactly as it was, so the web
 * seed editor still opens on the brand the admin picked.
 *
 * AUDIT. The write records `{ reason, changedBy, changedAt }` in the row's
 * `metadata` column — the same audit shape and field bag
 * (`SystemFlagAuditRequestFields`) every other internal config write uses.
 */

import { z } from 'zod'
import { SystemFlagAuditRequestFields } from '../../schemas/entities/system-flag'

/**
 * POST /internal/config/themes/rederive — request body.
 *
 * Every field is optional; an empty body is a LIVE re-derivation of every
 * school that has a stored seed pair. Pass `dry_run` first — the response
 * reports the exact before/after contrast per school, which is the thing worth
 * reading before writing 11 palettes.
 */
export const RederiveThemesRequestSchema = z.object({
  dry_run: z.boolean().default(false).describe('Compute and report exactly what WOULD change, writing nothing'),
  school_ids: z
    .array(z.string().min(1))
    .optional()
    .describe(
      'Restrict the run to these schools (public ID or slug). Omit to cover every school with a stored seed pair. ' +
        'Requested schools are resolved independently of the seed scan, so a real school with no brand reports ' +
        '`no-seeds` while a mistyped id reports `unknown-school` — never silently dropped.'
    ),
  ...SystemFlagAuditRequestFields
})

export type RederiveThemesRequest = z.infer<typeof RederiveThemesRequestSchema>

/**
 * Why a school was inspected but not repaired. Distinct from a failure: every
 * value here is an ordinary, expected state.
 */
export const RederiveThemeSkipReasonSchema = z.enum([
  /** The school has no `APP_THEME_SEEDS` row — nothing to re-derive FROM. */
  'no-seeds',
  /** The stored seed pair is malformed and cannot be parsed as two HSL tokens. */
  'invalid-seeds',
  /** The requested id matched no school. */
  'unknown-school'
])

export type RederiveThemeSkipReason = z.infer<typeof RederiveThemeSkipReasonSchema>

/**
 * The one measurement that says whether this repair was needed: how legible
 * `primary` is on the surface it is painted on, before and after. Reported for
 * dark mode, where the collision this endpoint repairs actually occurs (a
 * near-black brand seed against a force-laddered near-black background).
 */
export const RederiveThemeContrastSchema = z.object({
  darkPrimary: z.string().describe('The dark-mode `primary` token'),
  darkBackground: z.string().describe('The dark-mode `background` token'),
  darkPrimaryOnBackground: z
    .number()
    .describe('WCAG contrast ratio (1-21) of dark `primary` against dark `background`. 4.5 is the AA text bar')
})

/** What happened to one school. */
export const RederiveThemeSchoolResultSchema = z.object({
  schoolId: z.string().describe("School's public ID (CUID)"),
  schoolName: z.string().describe("School's name, so the operator can read the audit without a second lookup"),
  changed: z
    .boolean()
    .describe('Did this call rewrite the palette? False when it already matched, and always false under dry_run'),
  skipped: RederiveThemeSkipReasonSchema.nullable().describe(
    'Why the school was not repaired; null when it was inspected'
  ),
  error: z
    .string()
    .nullable()
    .describe(
      'Why the write FAILED for this school, null when it did not fail. A failed repair is never reported as an ' +
        'unchanged school: the run still returns 200 so one bad row cannot strand the rest, so this field (and the ' +
        "response's `failed` count) is what says the repair did not happen."
    ),
  before: RederiveThemeContrastSchema.nullable().describe('Contrast as stored, before this call; null when skipped'),
  after: RederiveThemeContrastSchema.nullable().describe('Contrast the current derivation produces; null when skipped')
})

/**
 * POST /internal/config/themes/rederive — response.
 *
 * Reports every school inspected, changed and skipped, so the operator can see
 * the exact blast radius and the contrast each palette landed on rather than a
 * bare success message.
 */
export const RederiveThemesResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    dryRun: z.boolean().describe('Was this a dry run? When true nothing was written'),
    inspected: z.number().describe('How many schools were looked at'),
    changed: z.number().describe('How many palettes were (or, under dry_run, would be) rewritten'),
    failed: z
      .number()
      .describe('How many schools failed to write. Non-zero means the run returned 200 but did NOT fully repair'),
    schools: z.array(RederiveThemeSchoolResultSchema)
  })
})

export type RederiveThemesResponse = z.infer<typeof RederiveThemesResponseSchema>
